import { ActionTabs, lottieAnimations, REMOTE_PERMISSIONS, StepIcon } from '@backrs_ui/common-ui';

import { useHasPermission } from '@backrs_ui/grow-ui/src/hooks/useHasPermission';
import { ResumeBuilderStep, SolutionSessionId } from '@backrs_ui/grow-ui/src/models/models';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getSessionState,
  getActiveLubavSession,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import { useGetResumeTemplateQuery } from '@backrs_ui/grow-ui/src/redux/solutionSession/solutionSession.api';

import { Box, SxProps, Theme, Typography } from '@mui/material';
import Lottie from 'lottie-react';
import ReactMarkdown from 'react-markdown';

import { getCurrentStep, getCurrentStepForDisplay, getStepRanges } from './resumeSteps';
import { copyResume } from './utils/copyResume';
import { createFullResume, ResumeContent } from './utils/createResumeSections';
import { downloadResume } from './utils/downloadResume';

const STEP_ANIMATION_MAP: Record<ResumeBuilderStep, object> = {
  basic_information: lottieAnimations.basicInformation,
  digging_for_gold: lottieAnimations.diggingForGold,
  polish: lottieAnimations.polishing,
};

const ResumeMarkdown = ({ text, fullResume }: { text: string; fullResume: boolean }) => {
  return (
    <ReactMarkdown
      components={{
        h2: ({ children }) => (
          <h2 style={headingStyle}>
            {
              <Typography variant="display-xs-semibold" sx={headingStyle}>
                {children}
              </Typography>
            }
          </h2>
        ),
        h3: ({ children }) => (
          <h3 style={sectionHeaderStyle}>
            {
              <Typography variant="text-md-semibold" sx={sectionHeaderStyle}>
                {children}
              </Typography>
            }
          </h3>
        ),
        ul: ({ children }) => (
          <ul style={listStyle}>
            {
              <Typography
                component="ul"
                variant={fullResume ? 'text-sm-regular' : 'text-xs-regular'}
                sx={listStyle}
              >
                {children}
              </Typography>
            }
          </ul>
        ),
        ol: ({ children }) => (
          <ol style={listStyle}>
            {
              <Typography
                component="ol"
                variant={fullResume ? 'text-sm-regular' : 'text-xs-regular'}
                sx={listStyle}
              >
                {children}
              </Typography>
            }
          </ol>
        ),
        li: ({ children }) => (
          <li style={listItemStyle}>
            {
              <Typography
                variant={fullResume ? 'text-sm-regular' : 'text-xs-regular'}
                sx={listItemStyle}
              >
                {children}
              </Typography>
            }
          </li>
        ),
        p: ({ children }) => (
          <Typography
            variant={fullResume ? 'text-sm-regular' : 'text-xs-regular'}
            sx={paragraphStyle}
          >
            {children}
          </Typography>
        ),
        hr: () => <hr style={dividerStyle} />,
      }}
    >
      {text}
    </ReactMarkdown>
  );
};

type ResumeBuilderProps = Readonly<{
  text: string;
  solutionSession: SolutionSessionId;
  isloading: boolean;
}>;

export const ResumeSection = ({ text, solutionSession, isloading }: ResumeBuilderProps) => {
  const { data: template } = useGetResumeTemplateQuery(solutionSession.split('.')[1]);
  const activeSession = useAppSelector(getActiveLubavSession);

  const betaMode = useHasPermission(REMOTE_PERMISSIONS.LUBAV_BETA);

  const { solutionSessionMessages } = useAppSelector(
    (state) => getSessionState(state, activeSession).coachLubav,
  );
  const currentStepValueForDisplay = getCurrentStepForDisplay(
    solutionSessionMessages,
    solutionSession,
  );
  const currentStepValue = getCurrentStep(solutionSessionMessages);

  const stepRanges = getStepRanges(currentStepValueForDisplay, solutionSession);

  if (!text) return null;

  const handleCopyResumeSection = (content: string) => {
    const contentElement = document.querySelector('[data-resume-content]');

    if (!contentElement) {
      return;
    }

    copyResume(content, contentElement as HTMLElement);
  };

  const handleDownloadResumeSection = async (content: string) => {
    try {
      await downloadResume(content);
    } catch (error) {
      console.error('Error generating DOCX:', error);
    }
  };

  return (
    <Box sx={sectionContainerSx}>
      <Box sx={stepIconsContainerSx}>
        {stepRanges.previousAndCurrentSteps.map((step) => (
          <StepIcon
            key={step.value}
            label={step.label}
            variant={step.value === currentStepValue ? 'current-previous' : 'done'}
          />
        ))}
      </Box>
      <Box sx={resumeSectionContainerSx} data-resume-content>
        <Box sx={actionTabsWrapperSx}>
          <ActionTabs
            actions={[
              ...(betaMode
                ? [
                    {
                      onClick: () => handleDownloadResumeSection(text || template || ''),
                      icon: 'Download01' as const,
                    },
                  ]
                : []),
              {
                onClick: () => handleCopyResumeSection(text || template || ''),
                icon: 'Copy01' as const,
              },
            ]}
            borderColor="Colors/Base/Black"
          />
        </Box>
        {isloading ? (
          <Box sx={animationContainerSx}>
            <Lottie
              animationData={STEP_ANIMATION_MAP[currentStepValueForDisplay]}
              loop={true}
              style={animationStyle}
            />
          </Box>
        ) : (
          <ResumeMarkdown text={text} fullResume={false} />
        )}
      </Box>
      <Box sx={stepIconsContainerSx}>
        {stepRanges.nextSteps.map((step) => (
          <StepIcon
            key={step.value}
            label={step.label}
            variant={step.value === currentStepValue ? 'current-next' : 'next'}
          />
        ))}
      </Box>
    </Box>
  );
};

export const Resume = (content: ResumeContent) => {
  const resume = createFullResume(content);

  return (
    <Box sx={fullResumeContainerSx} data-resume-content-full>
      <ResumeMarkdown text={resume} fullResume={true} />
    </Box>
  );
};

const resumeSectionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  position: 'relative',
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  padding: backrs.spacing['spacing-3xl'],
  fontFamily: 'Libre Baskerville, serif !important',
  '& *': {
    fontFamily: 'Libre Baskerville, serif !important',
  },
});

const fullResumeContainerSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  position: 'relative',
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  padding: backrs.spacing['spacing-6xl'],
  fontFamily: 'Libre Baskerville, serif !important',
  '& *': {
    fontFamily: 'Libre Baskerville, serif !important',
  },
});

const actionTabsWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  position: 'absolute',
  top: backrs.spacing['spacing-md'],
  right: backrs.spacing['spacing-md'],
  zIndex: 1,
});

const stepIconsContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
};

const sectionContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const listStyle: React.CSSProperties = {
  paddingLeft: '20px',
  margin: 0,
  marginBottom: '4px',
  marginTop: '-2px',
  marginLeft: '-10px',
};

const paragraphStyle: React.CSSProperties = {
  margin: 0,
  marginBottom: '8px',
};

const listItemStyle: React.CSSProperties = {
  marginBottom: '2px',
  marginTop: '2px',
};

const headingStyle: React.CSSProperties = {
  marginBottom: '-2px',
  marginTop: 0,
};

const sectionHeaderStyle: React.CSSProperties = {
  margin: 0,
  marginTop: '4px',
  marginBottom: '-3px',
};

const dividerStyle: React.CSSProperties = {
  border: 'none',
  height: '1px',
  backgroundColor: '#999',
  width: '100%',
  marginBottom: '8px',
};

const animationContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'center',
  height: '100%',
  width: '100%',
};

const animationStyle: React.CSSProperties = {
  maxHeight: '400px',
  height: '400px',
  width: 'auto',
};
